import { useQueryClient, useQuery } from "@tanstack/react-query";

const CART_KEY = "cart";
const COUPON_KEY = "cart_coupon";
const QR_KEY_STORAGE = "appliedQRKey";
const ORDER_DATA_KEY = "cart_order_data";

export type CartItem = {
  id: string; // تغيير من string | number إلى string فقط
  name: string;
  price: string;
  icon: string | JSX.Element;
  quantity: number;
  type?: "service" | "subscription";
  order_id?: number;
  price_id?: number;
  is_quantity_requestable?: boolean;
};

export type OrderData = {
  order_id: number;
  phone: string;
  name: string;
  coupon?: string;
};

// Type للـ QR Key المحفوظ
export type AppliedQRKey = {
  type: "qr_key";
  data: {
    created_at: string;
    key: string;
    subscription: number;
    service: number;
    is_used: boolean;
    points_spent: number;
    subscription_info: {
      id: number;
      size: "motorcycle" | "small" | "medium" | "large" | "extra_large";
    };
    service_info: {
      id: number;
      name: string;
    };
    service_size_info: {
      id: number;
      size: "motorcycle" | "small" | "medium" | "large" | "extra_large";
    };
    subscription_size_info: {
      id: number;
      name: string;
    };
  };
  key: string;
};

export function useCart() {
  const queryClient = useQueryClient();

  const loadCartFromStorage = (): CartItem[] => {
    const saved = localStorage.getItem(CART_KEY);
    return saved ? JSON.parse(saved) : [];
  };

  const loadCouponFromStorage = () => {
    const saved = localStorage.getItem(COUPON_KEY);
    return saved ? JSON.parse(saved) : null;
  };

  const loadQRKeyFromStorage = (): AppliedQRKey | null => {
    const saved = localStorage.getItem(QR_KEY_STORAGE);
    return saved ? JSON.parse(saved) : null;
  };

  const loadOrderDataFromStorage = (): OrderData | null => {
    const saved = localStorage.getItem(ORDER_DATA_KEY);
    return saved ? JSON.parse(saved) : null;
  };

  const { data: cart = [] } = useQuery<CartItem[]>({
    queryKey: [CART_KEY],
    queryFn: () => {
      return (
        queryClient.getQueryData<CartItem[]>([CART_KEY]) ||
        loadCartFromStorage()
      );
    },
    initialData: loadCartFromStorage(),
  });

  const { data: coupon = null } = useQuery({
    queryKey: [COUPON_KEY],
    queryFn: () => {
      return queryClient.getQueryData([COUPON_KEY]) || loadCouponFromStorage();
    },
    initialData: loadCouponFromStorage(),
  });

  const { data: qrKey = null } = useQuery<AppliedQRKey | null>({
    queryKey: [QR_KEY_STORAGE],
    queryFn: () => {
      return (
        queryClient.getQueryData<AppliedQRKey | null>([QR_KEY_STORAGE]) ||
        loadQRKeyFromStorage()
      );
    },
    initialData: loadQRKeyFromStorage(),
  });

  const { data: orderData = null } = useQuery<OrderData | null>({
    queryKey: [ORDER_DATA_KEY],
    queryFn: () => {
      return (
        queryClient.getQueryData<OrderData | null>([ORDER_DATA_KEY]) ||
        loadOrderDataFromStorage()
      );
    },
    initialData: loadOrderDataFromStorage(),
  });

  const saveCart = (items: CartItem[]) => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
    queryClient.setQueryData([CART_KEY], [...items]);
  };

  const saveCoupon = (coupon: any) => {
    localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
    queryClient.setQueryData([COUPON_KEY], coupon);
  };

  const saveQRKey = (qrKeyData: AppliedQRKey) => {
    localStorage.setItem(QR_KEY_STORAGE, JSON.stringify(qrKeyData));
    queryClient.setQueryData([QR_KEY_STORAGE], qrKeyData);
  };

  const saveOrderData = (data: OrderData | null) => {
    if (data) {
      localStorage.setItem(ORDER_DATA_KEY, JSON.stringify(data));
      queryClient.setQueryData([ORDER_DATA_KEY], data);
    } else {
      localStorage.removeItem(ORDER_DATA_KEY);
      queryClient.removeQueries({ queryKey: [ORDER_DATA_KEY] });
    }
  };

  const clearCart = () => {
    localStorage.removeItem(CART_KEY);
    localStorage.removeItem(COUPON_KEY);
    localStorage.removeItem(QR_KEY_STORAGE);
    localStorage.removeItem(ORDER_DATA_KEY);
    queryClient.removeQueries({ queryKey: [CART_KEY] });
    queryClient.removeQueries({ queryKey: [COUPON_KEY] });
    queryClient.removeQueries({ queryKey: [QR_KEY_STORAGE] });
    queryClient.removeQueries({ queryKey: [ORDER_DATA_KEY] });
  };

  const removeItem = (id: string) => {
    const updatedCart = cart.filter((item) => item.id !== id);
    saveCart(updatedCart);
  };

  const addItem = (newItem: CartItem) => {
    // حارس: منع أكثر من اشتراك واحد
    if (newItem.type === "subscription") {
      const existingSub = cart.find((i) => i.type === "subscription");
      if (existingSub) {
        return; // يمكن عرض toast من مكان الاستدعاء
      }
    }

    // حارس: منع أكثر من خدمة واحدة في السلة (فقط للخدمات التي لا تسمح بالتعدد)
    if (newItem.type === "service" && !newItem.is_quantity_requestable) {
      const existingService = cart.find(
        (i) => i.type === "service" && !i.is_quantity_requestable
      );
      if (existingService) {
        return; // يمكن عرض toast من مكان الاستدعاء
      }
    }

    // للخدمات التي تسمح بالتعدد، نزيد الكمية إذا كانت موجودة بالفعل
    if (newItem.type === "service" && newItem.is_quantity_requestable) {
      const existingService = cart.find((i) => i.id === newItem.id);
      if (existingService) {
        const updatedCart = cart.map((item) =>
          item.id === newItem.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
        saveCart(updatedCart);
        return;
      }
    }

    const updatedCart = [...cart, newItem];
    saveCart(updatedCart);
  };

  const updateItem = (id: string, updatedFields: Partial<CartItem>) => {
    const updatedCart = cart.map((item) =>
      item.id === id ? { ...item, ...updatedFields } : item
    );
    saveCart(updatedCart);
  };

  const applyCoupon = (couponData: any) => {
    saveCoupon(couponData);
  };

  const applyQRKey = (qrKeyData: AppliedQRKey) => {
    saveQRKey(qrKeyData);
  };

  const clearCoupon = () => {
    localStorage.removeItem(COUPON_KEY);
    queryClient.removeQueries({ queryKey: [COUPON_KEY] });
  };

  const clearQRKey = () => {
    localStorage.removeItem(QR_KEY_STORAGE);
    queryClient.removeQueries({ queryKey: [QR_KEY_STORAGE] });
  };

  const setOrderData = (data: OrderData | null) => {
    saveOrderData(data);
  };

  return {
    cart,
    coupon,
    qrKey,
    orderData,
    setCart: saveCart,
    clearCart,
    removeItem,
    addItem,
    updateItem,
    applyCoupon,
    applyQRKey,
    clearCoupon,
    clearQRKey,
    setOrderData,
  };
}
